import moment from "moment-timezone";
import Form, { List } from "rc-field-form";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "../../../../../components/Button";
import Field from "../../../../../components/Field";
import Select from "../../../../../components/Inputs/Select";
import Checkbox from "../../../../../components/Inputs/Checkbox";
import DateRangeFilters from "../../../../../components/DateRangeFilters";
import Loading from "../../../../../components/Loaders/Loading";
import Item from "./Item";
import { useExportReport, useFilterFields } from "../../../api";
import { useDateFilters } from "../../../../../components/DateRangeFilters/useDateFilters";
import { firstToUpper } from "../../../../../utils/helpers/string";

export const initialFilters = [{ member: "", operator: "", values: "" }];

export const isFilterValid = ({ member, operator, values }) => {
  if (Array.isArray(values) && values.length === 0) {
    return false;
  }
  return !!member && !!operator && ![undefined, null, ""].includes(values);
};

const mergeFilters = (filters, config) => {
  return config?.map((item) => {
    const value = filters?.find((filter) => filter.member === item.name);
    if (value) {
      return { ...value, ...item }
    } else {
      return item;
    }
  });
}

function Content({ report, filters, close }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { from, to, mode, payGroup, payPeriodGroup } = useDateFilters();

  const { filterFields, getFilterFields } = useFilterFields();
  const { loading, exportReport } = useExportReport();

  const processFilterValues = (values) => {
    if (!values) return [];

    const processValue = (v) => {
      if (v == null) return null;

      if (v instanceof Date || moment.isMoment(v)) {
        return moment(v).format("YYYY-MM-DD");
      }

      if (typeof v === "object") {
        if (v.id) {
          return {
            id: v?.id,
            code: v?.code,
            description: v?.description,
            glSegment: v?.glSegment,
            name: v?.name,
          };
        }
        return null;
      }

      if (typeof v === "string") {
        // Only parse the string as a date if it looks like a valid date format
        // if (moment(v, [moment.ISO_8601, "YYYY-MM-DD"], true).isValid()) {
        //   return moment(v).format("YYYY-MM-DD"); // valid date format, convert
        // }

        return v;
      }

      return String(v);
    };

    if (Array.isArray(values)) {
      return values.map(processValue).filter(Boolean);
    }

    if (typeof values === "string" && values.includes(",")) {
      return values
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    const result = processValue(values);
    return result ? [result] : [];
  };


  const config = useMemo(() => {
    const columnTypes = report?.reportData?.columns?.map((item) =>
      item?.name?.split(".")[0]
    );

    const fields = filterFields?.reduce((total, current) => {
      if (columnTypes?.includes(current?.name)) {
        const fields = current?.fields?.map((field) => ({ ...field, table: current?.name }));
        total.push(fields);
      }
      return total.flat();
    }, []);

    return fields?.filter((field) => field?.canFilter);
  }, [filterFields, report?.reportData?.columns]);

  const changeFieldMethod = useCallback(
    (name, operator) => {
      form.setFieldValue(["filters", name, "operator"], operator);
    },
    [form]
  );

  const changeFieldValue = useCallback(
    (name, value) => {
      form.setFieldValue(["filters", name, "values"], value);
    },
    [form]
  );

  const generateSubmit = useCallback((values) => {
    const isFilterValid = ({ member, operator, values }) => {
      if (Array.isArray(values) && values.length === 0) {
        return false;
      }
      return !!member && !!operator && ![undefined, null, ""].includes(values);
    };

    const columnFilters = values?.filters
      ?.map((filter) => ({
        ...filter,
        values: processFilterValues(filter?.values),
      }))
      ?.filter(isFilterValid);

    let filters;
    try {
      filters = JSON.parse(sessionStorage.getItem("filters")) || {};
    } catch (err) {
      filters = {};
    }

    filters["report-" + report?.id] = {
      ...filters["report-" + report?.id],
      columnFilters: columnFilters?.length > 0
        ? columnFilters
        : initialFilters
    };
    
    sessionStorage.setItem("filters", JSON.stringify(filters));
    let url = `/reports/view/${report?.id}?mode=${mode}&from=${moment(from).format("YYYY-MM-DD")}&to=${moment(to).format("YYYY-MM-DD")}`;
    if (mode === "pay-group") {
      url = url + `&payGroupPeriod=${payPeriodGroup}`;
      if (payGroup) url += `&payGroup=${payGroup?.code}`;
    }
    navigate(url);
  }, [report?.id, mode, from, to, navigate, payPeriodGroup, payGroup]);

  const exportSubmit = useCallback((values) => {
    const isFilterValid = ({ member, operator, values }) => {
      if (Array.isArray(values) && values.length === 0) {
        return false;
      }
      return !!member && !!operator && ![undefined, null, ""].includes(values);
    };

    const processedFilters = values?.filters
      ?.map((filter) => ({
        ...filter,
        values: processFilterValues(filter?.values),
      }))
      ?.filter(isFilterValid);

    const customFilters = processedFilters?.map((filter) => {
      return {
        ...filter,
        values: filter.values.map((value) => value?.id ? value.id : value)
      }
    });
    const fileType = values?.fileType || "xlsx";
    const data = {
      reportId: report?.id,
      fileType,
      customFilters,
      from: moment(from).format("YYYY-MM-DD"),
      to: moment(to).format("YYYY-MM-DD"),
      includeMeta: fileType === "xlsx" && !!values?.includeMeta,
    };
    exportReport(data, report?.name);

    close();
  }, [close, exportReport, from, report, to]);

  const onFinish = useCallback(
    (values) => {
      if (report?.type === 'export') {
        exportSubmit(values);
      } else {
        generateSubmit(values);
      }
    }, [report?.type, exportSubmit, generateSubmit]
  );

  useEffect(() => {
    const data = filters?.map((filter) => {
      const matchingItem = config?.find((item) => item.name === filter.member);

      // if (matchingItem) {
      //   return {
      //     ...filter,
      //     values: (
      //       (!matchingItem?.fieldType || matchingItem?.fieldType === "text") &&
      //       filter?.values &&
      //       Array.isArray(filter?.values)
      //     )
      //       ? moment(filter?.values?.[0], [moment.ISO_8601, "YYYY-MM-DD"], true).isValid() ? moment(filter?.values?.[0]).toDate() : filter?.values
      //       : moment(filter?.values, [moment.ISO_8601, "YYYY-MM-DD"], true).isValid() ? moment(filter?.values).toDate() : filter?.values,
      //   }
      // } else {

      //   return {
      //     ...filter,
      //     values: (
      //       filter?.values &&
      //       Array.isArray(filter?.values)
      //     )
      //       ? moment(filter?.values?.[0], [moment.ISO_8601, "YYYY-MM-DD"], true).isValid() ? moment(filter?.values?.[0]).toDate() : filter?.values
      //       : moment(filter?.values, [moment.ISO_8601, "YYYY-MM-DD"], true).isValid() ? moment(filter?.values).toDate() : filter?.values,
      //   }
      // }
      const isValidDate = (value) => value instanceof Date || moment.isMoment(value);
      const isValidDateString = (value) => moment(value, "YYYY-MM-DD", true).isValid();

      if (matchingItem) {
        return {
          ...filter,
          values: (
            (!matchingItem?.fieldType || matchingItem?.fieldType === "text") &&
            filter?.values &&
            Array.isArray(filter?.values)
          )
            ? ((isValidDate(filter?.values?.[0]) || isValidDateString(filter?.values?.[0]))
              ? moment(filter?.values?.[0]).toDate()
              : filter?.values)
            : ((isValidDate(filter?.values) || isValidDateString(filter?.values))
              ? moment(filter?.values).toDate()
              : filter?.values),
        }
      } else {
        return {
          ...filter,
          values: (
            filter?.values &&
            Array.isArray(filter?.values)
          )
            ? ((isValidDate(filter?.values?.[0]) || isValidDateString(filter?.values?.[0]))
              ? moment(filter?.values?.[0]).toDate()
              : filter?.values)
            : ((isValidDate(filter?.values) || isValidDateString(filter?.values))
              ? moment(filter?.values).toDate()
              : filter?.values),
        }
      }
    });
    
    form.setFieldValue("filters", data || initialFilters);
  }, [form, config, filters]);

  useEffect(() => {
    const controller = new AbortController();
    getFilterFields(controller);
    return () => controller.abort();
  }, [getFilterFields]);

  const initialValues = useMemo(() => {
    return filters ? mergeFilters(filters, config) : initialFilters;
  }, [filters, config]);

  useEffect(() => {
    if (mode === "pay-group" && payGroup) {
      let member = config?.find((item) => (item.name.toLowerCase()).includes("paygroup"));
      member = member ? `${member.table}.${member.name}` : "payGroup";
      const hasExisting = !!filters
        ?.find((filter) => ((filter?.member || "").toLowerCase() || "").includes("paygroup"));
      let newValue;

      if (payGroup?.id) {
        if (hasExisting) {
          newValue = filters.map((filter) => {
            if (!((filter?.member || "").toLowerCase() || "").includes("paygroup")) {
              return filter;
            }
            return {
              ...filter,
              values: [{ id: payGroup.id }],
            };
          });
        } else {
          const existingIndex = filters?.findIndex(
            (filter) => filter.values === "" || filter?.values?.length === 0
          );

          if (existingIndex === -1) {
            newValue = [
              {
                operator: "exact",
                member,
                values: [{ id: payGroup.id }],
              },
              ...(filters || []),
            ];
          } else {
            newValue = [...(filters || [])];
            newValue.splice(existingIndex, 1, {
              operator: "exact",
              member,
              values: [{ id: payGroup.id }],
            });
          }
        }
      } else {
        newValue = filters?.filter((filter) => {
          return !((filter?.member || "").toLowerCase() || "").includes("paygroup");
        });
      }

      if (JSON.stringify(newValue) !== JSON.stringify(filters)) {
        form.setFieldValue("filters", newValue);
      }
    }
  }, [config, filters, form, mode, payGroup]);

  useEffect(() => {
    form.setFieldValue("fileType", "xlsx");
  }, [form]);

  return (
    <Form
      initialValues={{ filters: initialValues }}
      form={form}
      onFinish={onFinish}
      className="d-flex align-items-start w-100 my-3"
    >
      <div className="d-flex flex-column w-100">
        <div className="d-flex flex-column gap-y-4 px-0 w-100 mb-4">
          {report?.type === "export" ? (
            <div className="">
              <h3>{t("export")}</h3>
            </div>
          ) : (
            <div className="">
              <h3>{t("generate")}</h3>
            </div>
          )}

          {report?.type === "export" && (
            <Field
              name="fileType"
            // label={t("file-type")}
            >
              <Select placeholder={t("file-type")}>
                <Select.Option value="xlsx" key="xlsx">
                  {firstToUpper(t("excel"))}
                </Select.Option>
                <Select.Option value="pdf" key="pdf">PDF</Select.Option>
              </Select>
            </Field>
          )}

          <DateRangeFilters
            setHasChangedDateFilter={() => { }}
          />
        </div>
        <div className="d-flex flex-column gap-y-4 px-0 w-100 mb-4">
          <List name="filters">
            {(fields, { add, remove }) =>
              fields.map(({ name, key }) => (
                <Item
                  changeFieldMethod={changeFieldMethod}
                  changeFieldValue={changeFieldValue}
                  config={config}
                  name={name}
                  key={key}
                  form={form}
                  add={() => add({}, name + 1)}
                  remove={() => remove(name)}
                />
              ))
            }
          </List>
        </div>

        {report?.type === "export" && (
          <Field shouldUpdate className="mb-4">
            {() => {
              if (form.getFieldValue("fileType") === "pdf") {
                return null;
              }
              return (
                <div>
                  <Field
                    name="includeMeta"
                    valuePropName="checked"
                    className="mb-1"
                  >
                    <Checkbox id="include-meta">
                      {t("include-report-header")}
                    </Checkbox>
                  </Field>
                  <div className="text-muted small pl-4">
                    {t("include-report-header-help")}
                  </div>
                </div>
              );
            }}
          </Field>
        )}

        <div iv className="d-flex justify-content-end">
          <Button color="primary">
            {loading ? <Loading /> : report?.type === "export" ? t("export") : t("generate")}
          </Button>

          <Button
            color="secondary"
            onClick={close}
          >
            {t("cancel")}
          </Button>
        </div>
      </div>
    </Form>
  );
}

export default Content;
